import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Optional,
  Output,
  Self,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { isPresent } from '@mtna/core-ts';

import { RDS_NULL_PARAMETER_OPTIONS, RdsNullParameter, RdsNullParameterOption } from '../core/models/filter/null-filter.model';

const noop = () => {
  /* Do nothing */
};

@Component({
  selector: 'rds-c-null-filter',
  templateUrl: './null-filter.component.html',
  styleUrls: ['./null-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RdsNullFilterComponent implements ControlValueAccessor {
  /** Whether the control is disabled, default to false */
  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(value: boolean | string) {
    const newValue = coerceBooleanProperty(value);
    if (newValue !== this.disabled) {
      this._disabled = newValue;
      this._changeDetectorRef.markForCheck();
    }
  }
  private _disabled = false;

  /** Whether the control is required, defaults to false */
  @Input() required = false;
  /** Label for mat-select, defaults to null */
  @Input() selectLabel: string | null = null;

  /** The selected null parameter and value for this control, defaults to null/unselected */
  @Input()
  set value(value: RdsNullParameter | undefined) {
    if (value !== this.value) {
      this._value = value;
      this._changeDetectorRef.markForCheck();
    }
  }
  get value(): RdsNullParameter | undefined {
    return this._value;
  }
  private _value: RdsNullParameter | undefined;

  /** Changed value of the form control */
  @Output() readonly selectionChange = new EventEmitter<RdsNullParameter | null>();

  _options: Array<RdsNullParameterOption> = [...RDS_NULL_PARAMETER_OPTIONS];

  /** `View -> model callback called when value changes` */
  _onChange: (value: unknown) => void = noop;

  /** `View -> model callback called when select has been touched` */
  _onTouched = noop;

  constructor(
    @Self() @Optional() public _control: NgControl,
    elementRef: ElementRef<HTMLElement>,
    private _changeDetectorRef: ChangeDetectorRef,
    private _focusMonitor: FocusMonitor
  ) {
    if (this._control) {
      this._control.valueAccessor = this;
    }
    this._focusMonitor.monitor(elementRef, true).subscribe((focusOrigin) => {
      if (!focusOrigin) {
        // When a focused element becomes disabled, the browser *immediately* fires a blur event.
        // Angular does not expect events to be raised during change detection, so any state change
        // (such as a form control's 'ng-touched') will cause a changed-after-checked error.
        // See https://github.com/angular/angular/issues/17793. To work around this, we defer
        // telling the form control it has been touched until the next tick.
        Promise.resolve().then(() => this._onTouched());
      }
    });
  }

  /**
   * Saves a callback function to be invoked when the select's value
   * changes from user input. Part of the ControlValueAccessor interface
   * required to integrate with Angular's core forms API.
   *
   * @param fn Callback to be triggered when the value changes.
   */
  registerOnChange(fn: (value: unknown) => void): void {
    this._onChange = fn;
  }

  /**
   * Saves a callback function to be invoked when the select is blurred
   * by the user. Part of the ControlValueAccessor interface required
   * to integrate with Angular's core forms API.
   *
   * @param fn Callback to be triggered when the component has been touched.
   */
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(obj: RdsNullParameter | undefined): void {
    this.value = obj;
  }

  _emitChangeEvent(newValue: RdsNullParameter | null) {
    this.value = isPresent(newValue) ? newValue : undefined;
    this._onChange(this.value);
    this.selectionChange.emit(this.value);
  }
}
